import { useState, useCallback, useMemo } from 'react';

/**
 * Hook para manejar selección múltiple en tablas
 * @param {Array} data - Array de objetos con campo 'id'
 * @returns {Object} - Estado y funciones de selección
 */
export const useBulkSelection = (data = []) => {
  const [selectedIds, setSelectedIds] = useState(new Set());

  // IDs disponibles en los datos actuales
  const availableIds = useMemo(() => new Set(data.map(item => item.id)), [data]);

  // Verificar si todos están seleccionados
  const isAllSelected = useMemo(() => {
    if (availableIds.size === 0) return false;
    return [...availableIds].every(id => selectedIds.has(id));
  }, [availableIds, selectedIds]);

  // Verificar si algunos están seleccionados (para indeterminate)
  const isSomeSelected = useMemo(() => {
    if (availableIds.size === 0) return false;
    const selectedCount = [...availableIds].filter(id => selectedIds.has(id)).length;
    return selectedCount > 0 && selectedCount < availableIds.size;
  }, [availableIds, selectedIds]);

  // Toggle selección individual
  const toggleRow = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Seleccionar/deseleccionar todos
  const toggleAll = useCallback(() => {
    if (isAllSelected) {
      // Deseleccionar todos los de la página actual
      setSelectedIds(prev => {
        const next = new Set(prev);
        availableIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      // Seleccionar todos los de la página actual
      setSelectedIds(prev => {
        const next = new Set(prev);
        availableIds.forEach(id => next.add(id));
        return next;
      });
    }
  }, [isAllSelected, availableIds]);

  // Limpiar selección
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Verificar si un ID está seleccionado
  const isSelected = useCallback((id) => {
    return selectedIds.has(id);
  }, [selectedIds]);

  // Obtener array de IDs seleccionados
  const selectedArray = useMemo(() => [...selectedIds], [selectedIds]);

  // Cantidad seleccionada
  const selectedCount = selectedIds.size;

  return {
    selectedIds: selectedArray,
    selectedCount,
    isAllSelected,
    isSomeSelected,
    isSelected,
    toggleRow,
    toggleAll,
    clearSelection,
  };
};

export default useBulkSelection;
